/**
 * RevenueArpuChart — revenue bars (gold) + ARPU line on a right axis, in the
 * profile chart anatomy (sentence, range slider, two-line ticks).
 */
import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatArpu, formatUsd, formatUsdCompact, prettyQuarter } from '@/lib/format';
import { useResolvedColors } from '@/lib/useResolvedColor';
import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import { QuarterRangeSlider } from './QuarterRangeSlider';
import { ChartSummary, Emph } from './ChartSummary';
import { QuarterTick, useQuarterWindow, profileChartStyles as s } from './ProfileSection';

interface HistoryPoint {
  quarter: string;
  revenue: number;
  arpu: number;
}

export interface RevenueArpuChartProps {
  platform: string;
  /** Scoped history, oldest → newest. */
  history: HistoryPoint[];
  /** "UCAN" / "all regions". */
  scopeLabel: string;
}

const LABEL_ALL_MAX = 12;

export function RevenueArpuChart({ platform, history, scopeLabel }: RevenueArpuChartProps) {
  const allPoints = useMemo(
    () => history.map((h) => ({ label: prettyQuarter(h.quarter), revenue: h.revenue, arpu: h.arpu })),
    [history],
  );
  const [[lo, hi], setRange] = useQuarterWindow(allPoints.length);
  const points = allPoints.slice(lo, hi + 1);
  const [gold, line] = useResolvedColors(['var(--accent-secondary)', 'var(--text-primary)']);
  const showAllLabels = points.length <= LABEL_ALL_MAX;

  const first = points[0];
  const last = points[points.length - 1];
  const arpuMove = first && last && first.arpu ? ((last.arpu - first.arpu) / first.arpu) * 100 : undefined;

  return (
    <>
      <ChartSummary className={s.summary}>
        {last ? (
          <>
            <Emph>{platform}</Emph> earned <Emph>{formatUsdCompact(last.revenue)}</Emph> in {scopeLabel} in{' '}
            <Emph>{last.label}</Emph> at <Emph>{formatArpu(last.arpu)}</Emph> per subscriber
            {arpuMove !== undefined && points.length > 1 && (
              <>
                , with ARPU {arpuMove >= 0 ? 'up' : 'down'} <Emph>{Math.abs(arpuMove).toFixed(1)}%</Emph> since{' '}
                {first.label}
              </>
            )}
            .
          </>
        ) : (
          '—'
        )}
      </ChartSummary>

      <div className={s.body}>
        {allPoints.length > 1 && (
          <QuarterRangeSlider
            count={allPoints.length}
            value={[lo, hi]}
            onChange={setRange}
            labels={allPoints.map((p) => p.label)}
          />
        )}
        {points.length ? (
          <>
            <div className={s.plot}>
              <span className={`${s.yAxisTitle} ${s.yAxisTitleLeft}`}>Revenue (USD)</span>
              <ResponsiveContainer width="100%" height={360}>
                <ComposedChart data={points} margin={{ top: 24, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    axisLine={{ stroke: 'var(--border-default)' }}
                    tickLine={false}
                    interval={0}
                    height={46}
                    tick={QuarterTick}
                  />
                  <YAxis
                    yAxisId="rev"
                    className={s.axis}
                    axisLine={false}
                    tickLine={false}
                    width={64}
                    tickFormatter={(v) => formatUsdCompact(Number(v))}
                  />
                  <YAxis
                    yAxisId="arpu"
                    orientation="right"
                    className={s.axis}
                    axisLine={false}
                    tickLine={false}
                    width={48}
                    domain={['auto', 'auto']}
                    tickFormatter={(v) => formatArpu(Number(v))}
                  />
                  <Tooltip
                    cursor={{ fill: 'var(--bg-hover)', opacity: 0.4 }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as (typeof points)[number];
                      return (
                        <ChartTooltip
                          title={label}
                          rows={[
                            { label: 'Revenue', value: formatUsd(p.revenue), color: gold },
                            { label: 'ARPU', value: formatArpu(p.arpu), color: line },
                          ]}
                        />
                      );
                    }}
                  />
                  <Bar yAxisId="rev" dataKey="revenue" fill={gold} radius={[2, 2, 0, 0]}>
                    <LabelList
                      dataKey="revenue"
                      content={(props) => {
                        const { x, y, width, value, index } = props as {
                          x: number; y: number; width: number; value: number; index: number;
                        };
                        if (!showAllLabels && index !== points.length - 1) return null;
                        return (
                          <text x={x + width / 2} y={y - 6} textAnchor="middle" className={s.valueLabel}>
                            {formatUsdCompact(value)}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                  <Line
                    yAxisId="arpu"
                    dataKey="arpu"
                    stroke={line}
                    strokeWidth={2}
                    dot={{ r: 3, fill: line, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
              <span className={`${s.yAxisTitle} ${s.yAxisTitleRight}`}>ARPU (USD)</span>
            </div>
            <ChartLegend
              items={[
                { label: 'Revenue', color: gold },
                { label: 'ARPU', color: line, shape: 'line' },
              ]}
            />
          </>
        ) : (
          <div className={s.empty}>No revenue data available</div>
        )}
      </div>
    </>
  );
}
